/**
 * RES-35 — Insumos típicos por rubro para el wizard de inventario (paso 1).
 *
 * Cada rubro precarga entre 15 y 30 insumos con `kind`, `unit` (canónica: un, g, kg, ml, l),
 * `category` y `defaultMinStock` (en la unidad del insumo). El dueño desmarca lo que no usa y
 * suma los propios. Los nombres son datos del catálogo del restaurante (quedan en su idioma de
 * carga); el nombre y la descripción del rubro salen de i18n (`inventory.wizard.rubros.{id}`).
 */

const ing = (name, unit, category, defaultMinStock) => ({
  name,
  kind: 'INGREDIENT',
  unit,
  category,
  defaultMinStock,
});
const resale = (name, category, defaultMinStock) => ({
  name,
  kind: 'RESALE',
  unit: 'un',
  category,
  defaultMinStock,
});
const pack = (name, defaultMinStock) => ({
  name,
  kind: 'PACKAGING',
  unit: 'un',
  category: 'Descartables',
  defaultMinStock,
});

const DRINKS = [
  resale('Agua mineral 500 ml', 'Bebidas', 24),
  resale('Gaseosa línea cola 500 ml', 'Bebidas', 24),
  resale('Gaseosa lima-limón 500 ml', 'Bebidas', 12),
];

export const INVENTORY_TEMPLATES = [
  {
    id: 'pizzeria',
    icon: 'Pizza',
    items: [
      ing('Harina 000', 'kg', 'Secos', 25),
      ing('Levadura fresca', 'g', 'Secos', 500),
      ing('Sal fina', 'kg', 'Secos', 2),
      ing('Azúcar', 'kg', 'Secos', 2),
      ing('Aceite de girasol', 'l', 'Secos', 5),
      ing('Aceite de oliva', 'l', 'Secos', 2),
      ing('Masa de pizza', 'g', 'Elaborados', 5000),
      ing('Salsa de tomate', 'ml', 'Elaborados', 3000),
      ing('Tomate triturado', 'kg', 'Verduras', 10),
      ing('Muzzarella', 'kg', 'Lácteos', 10),
      ing('Queso parmesano', 'kg', 'Lácteos', 1),
      ing('Queso roquefort', 'kg', 'Lácteos', 1),
      ing('Jamón cocido', 'kg', 'Fiambres', 3),
      ing('Longaniza', 'kg', 'Fiambres', 1),
      ing('Cebolla', 'kg', 'Verduras', 5),
      ing('Morrón', 'kg', 'Verduras', 2),
      ing('Tomate', 'kg', 'Verduras', 5),
      ing('Albahaca', 'g', 'Verduras', 200),
      ing('Aceitunas verdes', 'kg', 'Conservas', 1),
      ing('Orégano', 'g', 'Condimentos', 200),
      ing('Huevo', 'un', 'Frescos', 30),
      pack('Caja de pizza', 50),
      pack('Servilletas', 200),
      ...DRINKS,
      resale('Cerveza lata 473 ml', 'Bebidas', 24),
    ],
  },
  {
    id: 'parrilla',
    icon: 'Beef',
    items: [
      ing('Asado', 'kg', 'Carnes', 15),
      ing('Vacío', 'kg', 'Carnes', 10),
      ing('Entraña', 'kg', 'Carnes', 5),
      ing('Bife de chorizo', 'kg', 'Carnes', 10),
      ing('Pollo', 'kg', 'Carnes', 10),
      ing('Chorizo', 'un', 'Achuras', 40),
      ing('Morcilla', 'un', 'Achuras', 20),
      ing('Molleja', 'kg', 'Achuras', 3),
      ing('Provoleta', 'un', 'Lácteos', 10),
      ing('Carbón', 'kg', 'Insumos de parrilla', 30),
      ing('Sal gruesa', 'kg', 'Condimentos', 3),
      ing('Chimichurri', 'ml', 'Elaborados', 2000),
      ing('Papa', 'kg', 'Verduras', 20),
      ing('Lechuga', 'kg', 'Verduras', 3),
      ing('Tomate', 'kg', 'Verduras', 5),
      ing('Cebolla', 'kg', 'Verduras', 5),
      ing('Aceite de girasol', 'l', 'Secos', 10),
      ing('Pan francés', 'un', 'Panificados', 40),
      pack('Servilletas', 200),
      ...DRINKS,
      resale('Vino tinto malbec 750 ml', 'Bebidas', 12),
      resale('Cerveza botella 1 l', 'Bebidas', 12),
    ],
  },
  {
    id: 'cafeteria',
    icon: 'Coffee',
    items: [
      ing('Café en grano', 'kg', 'Café e infusiones', 3),
      ing('Leche entera', 'l', 'Lácteos', 20),
      ing('Leche descremada', 'l', 'Lácteos', 6),
      ing('Bebida vegetal de almendras', 'l', 'Lácteos', 3),
      ing('Crema de leche', 'ml', 'Lácteos', 2000),
      ing('Azúcar', 'kg', 'Secos', 3),
      ing('Edulcorante', 'un', 'Secos', 200),
      ing('Cacao en polvo', 'kg', 'Secos', 1),
      ing('Té en saquitos', 'un', 'Café e infusiones', 100),
      ing('Medialuna', 'un', 'Panificados', 48),
      ing('Pan de molde', 'un', 'Panificados', 4),
      ing('Jamón cocido', 'kg', 'Fiambres', 2),
      ing('Queso de máquina', 'kg', 'Lácteos', 2),
      ing('Manteca', 'kg', 'Lácteos', 1),
      ing('Dulce de leche', 'kg', 'Secos', 2),
      ing('Mermelada', 'kg', 'Secos', 1),
      ing('Naranja', 'kg', 'Frutas', 10),
      ing('Budín', 'un', 'Pastelería', 4),
      pack('Vaso térmico', 100),
      pack('Tapa para vaso', 100),
      pack('Revolvedor', 200),
      pack('Servilletas', 300),
      resale('Agua mineral 500 ml', 'Bebidas', 24),
      resale('Jugo exprimido envasado', 'Bebidas', 12),
    ],
  },
  {
    id: 'bar',
    icon: 'Wine',
    items: [
      ing('Fernet', 'ml', 'Bebidas alcohólicas', 3000),
      ing('Gin', 'ml', 'Bebidas alcohólicas', 2000),
      ing('Vodka', 'ml', 'Bebidas alcohólicas', 2000),
      ing('Ron blanco', 'ml', 'Bebidas alcohólicas', 1500),
      ing('Aperitivo rojo', 'ml', 'Bebidas alcohólicas', 2000),
      ing('Vermut', 'ml', 'Bebidas alcohólicas', 1500),
      ing('Espumante', 'ml', 'Bebidas alcohólicas', 3000),
      ing('Tónica', 'ml', 'Mixers', 6000),
      ing('Gaseosa cola (línea)', 'ml', 'Mixers', 10000),
      ing('Soda', 'ml', 'Mixers', 6000),
      ing('Jugo de naranja', 'ml', 'Mixers', 3000),
      ing('Limón', 'kg', 'Frutas', 3),
      ing('Lima', 'kg', 'Frutas', 1),
      ing('Menta', 'g', 'Frutas', 200),
      ing('Azúcar', 'kg', 'Secos', 2),
      ing('Hielo', 'kg', 'Insumos de barra', 30),
      ing('Almíbar', 'ml', 'Elaborados', 1000),
      ing('Maní', 'kg', 'Snacks', 2),
      ing('Papas fritas de paquete', 'un', 'Snacks', 20),
      pack('Sorbete', 300),
      pack('Servilletas', 200),
      resale('Cerveza lata 473 ml', 'Bebidas', 48),
      resale('Cerveza botella 1 l', 'Bebidas', 24),
      resale('Agua mineral 500 ml', 'Bebidas', 24),
      resale('Energizante lata', 'Bebidas', 12),
    ],
  },
  {
    id: 'hamburgueseria',
    icon: 'Sandwich',
    items: [
      ing('Carne picada', 'kg', 'Carnes', 10),
      ing('Medallón de carne', 'un', 'Carnes', 60),
      ing('Pan de hamburguesa', 'un', 'Panificados', 60),
      ing('Queso cheddar', 'un', 'Lácteos', 100),
      ing('Panceta', 'kg', 'Fiambres', 2),
      ing('Lechuga', 'kg', 'Verduras', 3),
      ing('Tomate', 'kg', 'Verduras', 5),
      ing('Cebolla', 'kg', 'Verduras', 5),
      ing('Pepinillos', 'kg', 'Conservas', 1),
      ing('Papas bastón congeladas', 'kg', 'Congelados', 20),
      ing('Aceite de girasol', 'l', 'Secos', 20),
      ing('Sal fina', 'kg', 'Secos', 2),
      ing('Mayonesa', 'ml', 'Salsas', 3000),
      ing('Kétchup', 'ml', 'Salsas', 3000),
      ing('Mostaza', 'ml', 'Salsas', 2000),
      ing('Salsa barbacoa', 'ml', 'Salsas', 1000),
      ing('Huevo', 'un', 'Frescos', 30),
      pack('Caja de hamburguesa', 100),
      pack('Bolsa de papel', 100),
      pack('Servilletas', 300),
      ...DRINKS,
      resale('Cerveza lata 473 ml', 'Bebidas', 24),
    ],
  },
  {
    id: 'sushi',
    icon: 'Fish',
    items: [
      ing('Arroz para sushi', 'kg', 'Secos', 10),
      ing('Vinagre de arroz', 'ml', 'Secos', 2000),
      ing('Azúcar', 'kg', 'Secos', 2),
      ing('Sal fina', 'kg', 'Secos', 1),
      ing('Alga nori', 'un', 'Secos', 100),
      ing('Salmón rosado', 'kg', 'Pescados', 5),
      ing('Langostino', 'kg', 'Pescados', 2),
      ing('Kanikama', 'kg', 'Pescados', 1),
      ing('Queso crema', 'kg', 'Lácteos', 2),
      ing('Palta', 'kg', 'Verduras', 3),
      ing('Pepino', 'kg', 'Verduras', 1),
      ing('Cebolla de verdeo', 'kg', 'Verduras', 1),
      ing('Sésamo', 'g', 'Secos', 500),
      ing('Panko', 'kg', 'Secos', 1),
      ing('Salsa de soja', 'ml', 'Salsas', 3000),
      ing('Jengibre encurtido', 'g', 'Conservas', 500),
      ing('Wasabi', 'g', 'Condimentos', 200),
      ing('Salsa teriyaki', 'ml', 'Salsas', 1000),
      pack('Bandeja de sushi', 100),
      pack('Palitos', 200),
      pack('Servilletas', 200),
      ...DRINKS,
    ],
  },
  {
    id: 'heladeria',
    icon: 'IceCreamCone',
    items: [
      ing('Base de helado de crema', 'kg', 'Bases', 20),
      ing('Base de helado al agua', 'kg', 'Bases', 10),
      ing('Leche entera', 'l', 'Lácteos', 20),
      ing('Crema de leche', 'l', 'Lácteos', 10),
      ing('Azúcar', 'kg', 'Secos', 25),
      ing('Dulce de leche', 'kg', 'Secos', 10),
      ing('Chocolate cobertura', 'kg', 'Secos', 5),
      ing('Cacao en polvo', 'kg', 'Secos', 2),
      ing('Frutilla', 'kg', 'Frutas', 5),
      ing('Limón', 'kg', 'Frutas', 3),
      ing('Banana', 'kg', 'Frutas', 5),
      ing('Almendras', 'kg', 'Frutos secos', 1),
      ing('Estabilizante', 'g', 'Secos', 1000),
      ing('Salsa de chocolate', 'ml', 'Salsas', 2000),
      ing('Rocklets', 'g', 'Toppings', 1000),
      pack('Cucurucho', 200),
      pack('Vasito', 200),
      pack('Pote 1/4 kg', 100),
      pack('Pote 1/2 kg', 100),
      pack('Pote 1 kg', 50),
      pack('Cucharita', 500),
      resale('Agua mineral 500 ml', 'Bebidas', 12),
    ],
  },
  {
    id: 'panaderia',
    icon: 'Croissant',
    items: [
      ing('Harina 000', 'kg', 'Secos', 50),
      ing('Harina 0000', 'kg', 'Secos', 25),
      ing('Levadura fresca', 'g', 'Secos', 1000),
      ing('Sal fina', 'kg', 'Secos', 3),
      ing('Azúcar', 'kg', 'Secos', 10),
      ing('Azúcar impalpable', 'kg', 'Secos', 2),
      ing('Manteca', 'kg', 'Lácteos', 10),
      ing('Margarina', 'kg', 'Secos', 10),
      ing('Grasa vacuna', 'kg', 'Secos', 5),
      ing('Huevo', 'un', 'Frescos', 90),
      ing('Leche entera', 'l', 'Lácteos', 10),
      ing('Dulce de leche', 'kg', 'Secos', 5),
      ing('Membrillo', 'kg', 'Secos', 3),
      ing('Crema pastelera', 'kg', 'Elaborados', 3),
      ing('Esencia de vainilla', 'ml', 'Secos', 500),
      ing('Polvo de hornear', 'g', 'Secos', 1000),
      ing('Chocolate cobertura', 'kg', 'Secos', 2),
      pack('Bolsa de papel', 300),
      pack('Caja de torta', 30),
      pack('Bandeja de cartón', 100),
      resale('Agua mineral 500 ml', 'Bebidas', 12),
      resale('Gaseosa línea cola 500 ml', 'Bebidas', 12),
    ],
  },
];

/**
 * Rubro por id.
 * @param {string} id - Id del rubro
 * @returns {Object|null} Rubro
 */
export const getInventoryTemplate = (id) =>
  INVENTORY_TEMPLATES.find((tpl) => tpl.id === id) || null;
